import React, { useState, useRef, useEffect } from 'react';
import './OpenQuestionsExercise.css';
import { fetchOpenAIText } from '../../utils/openaiClient';

const MAX_QUESTIONS = 10;

const OQIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
    <circle cx="12" cy="17" r="0.5" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="10" />
  </svg>
);

const EditIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const OpenQuestionsExercise = ({ questions, title, level, onClose, updateAttributes, node, editor, getPos, isStudent = false, myState = {}, otherState = {}, updateState = () => {} }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableQuestions, setEditableQuestions] = useState([...questions]);
  const [editableReferenceText, setEditableReferenceText] = useState((node?.attrs?.referenceText || ''));
  const [referenceExpanded, setReferenceExpanded] = useState(false);
  const [showSuggested, setShowSuggested] = useState(false);
  const addBtnRef = useRef(null);
  const dotsScrollRef = useRef(null);
  const hintRunRef = useRef(false);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewResults, setReviewResults] = useState(null);
  const [showReview, setShowReview] = useState(false);
  const syncedRef = React.useRef(false);
  const prevResetAtRef = React.useRef(null);
  useEffect(() => {
    if (syncedRef.current) return;
    const hasAnswers = myState.answers && Object.keys(myState.answers).length > 0;
    const hasReview = myState.showReview && Array.isArray(myState.reviewResults);
    if (hasAnswers || hasReview) {
      syncedRef.current = true;
      if (myState.answers) setUserAnswers(myState.answers);
      if (hasReview) {
        setReviewResults(myState.reviewResults);
        setShowReview(true);
      }
    }
  }, [myState.answers, myState.showReview, myState.reviewResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // Real-time typing sync: mirror other side's answers into local textarea
  useEffect(() => {
    if (!otherState.answers) return;
    setUserAnswers(prev => {
      const merged = { ...prev };
      let changed = false;
      for (const [k, v] of Object.entries(otherState.answers)) {
        if (merged[k] !== v) { merged[k] = v; changed = true; }
      }
      return changed ? merged : prev;
    });
  }, [otherState.answers]); // eslint-disable-line

  // Sync review results from other side
  useEffect(() => {
    if (!otherState.showReview || !Array.isArray(otherState.reviewResults)) return;
    setReviewResults(otherState.reviewResults);
    setShowReview(true);
  }, [otherState.showReview]); // eslint-disable-line

  // Navigation sync
  useEffect(() => {
    if (typeof otherState.currentQuestion !== 'number') return;
    setCurrentQuestionIndex(otherState.currentQuestion);
  }, [otherState.currentQuestion]); // eslint-disable-line

  // Try Again sync
  useEffect(() => {
    const resetAt = otherState.resetAt;
    if (!resetAt || resetAt === prevResetAtRef.current) return;
    prevResetAtRef.current = resetAt;
    setUserAnswers({});
    setShowReview(false);
    setReviewResults(null);
    setCurrentQuestionIndex(0);
    setReviewError('');
    updateState({ answers: {}, showReview: false, reviewResults: null });
  }, [otherState.resetAt]); // eslint-disable-line

  const handleTryAgain = () => {
    setUserAnswers({});
    setShowReview(false);
    setReviewResults(null);
    setCurrentQuestionIndex(0);
    setReviewError('');
    updateState({ answers: {}, showReview: false, reviewResults: null, resetAt: Date.now() });
  };

  const handleAnswerChange = (questionIndex, answer) => {
    setUserAnswers(prev => {
      const next = { ...prev, [questionIndex]: answer };
      updateState({ answers: next });
      return next;
    });
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      const nextIdx = currentQuestionIndex + 1;
      setCurrentQuestionIndex(nextIdx);
      updateState({ currentQuestion: nextIdx });
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      const prevIdx = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prevIdx);
      updateState({ currentQuestion: prevIdx });
    }
  };

  const handleSave = () => {
    setIsEditing(false);
    if (updateAttributes) {
      updateAttributes({
        questions: editableQuestions,
        title: editableTitle,
        level,
        referenceText: editableReferenceText,
      });
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableTitle(title);
    setEditableQuestions([...questions]);
    setEditableReferenceText(node?.attrs?.referenceText || '');
  };

  const updateQuestionField = (index, field, value) => {
    setEditableQuestions(prev => prev.map((q, i) => i === index ? { ...q, [field]: value } : q));
  };

  const updateHideSuggested = (index, hide) => {
    setEditableQuestions(prev => prev.map((q, i) => i === index ? { ...q, hideSuggestedAnswer: hide } : q));
  };

  const handleAddQuestion = () => {
    if (editableQuestions.length >= MAX_QUESTIONS) return;
    const newQuestion = { question: '', suggestedAnswer: '', hideSuggestedAnswer: false };
    setEditableQuestions(prev => {
      const updated = [...prev, newQuestion];
      setCurrentQuestionIndex(updated.length - 1);
      return updated;
    });
  };

  const handleDeleteQuestion = (questionIndex) => {
    if (editableQuestions.length <= 1) return; // Не удаляем последний вопрос

    setEditableQuestions(prev => {
      const updated = prev.filter((_, index) => index !== questionIndex);
      // Корректируем индекс текущего вопроса
      if (currentQuestionIndex >= questionIndex) {
        setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1));
      }
      return updated;
    });
  };
  // микро-хинт прокрутки как в ABCD (один раз)
  useEffect(() => {
    if (hintRunRef.current) return;
    if (!dotsScrollRef.current) return;
    if (editableQuestions.length <= 5) return;
    hintRunRef.current = true;
    const scroller = dotsScrollRef.current;
    const delta = 24;
    try {
      const start = scroller.scrollLeft;
      const t1 = setTimeout(() => {
        scroller.scrollTo({ left: start + delta, behavior: 'smooth' });
        const t2 = setTimeout(() => {
          scroller.scrollTo({ left: start, behavior: 'smooth' });
        }, 500);
        (hintRunRef.currentCleanup = () => clearTimeout(t2));
      }, 120);
      return () => {
        clearTimeout(t1);
        if (hintRunRef.currentCleanup) hintRunRef.currentCleanup();
      };
    } catch (_) {}
  }, [editableQuestions.length]);

  // обновление фейдов по скроллу/resize
  useEffect(() => {
    const el = dotsScrollRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setShowLeftFade(el.scrollLeft > 2);
      setShowRightFade(el.scrollLeft < max - 2);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  const userAnswer = userAnswers[currentQuestionIndex] || '';

  const allAnswered = editableQuestions.every((_, idx) => (userAnswers[idx] || '').trim().length > 0);

  function buildEvaluationPrompt() {
    const referenceText = (node?.attrs?.referenceText || '').toString();
    const payload = editableQuestions.map((q, idx) => ({
      index: idx,
      question: (q?.question || '').toString(),
      student_answer: (userAnswers[idx] || '').toString()
    }));
    const lvl = (level || 'B1');
    return [
      'You are an expert ESL teacher and CEFR assessor.',
      `Evaluate the following student answers according to CEFR level ${lvl}.`,
      referenceText ? 'Most important: each answer MUST be factually grounded in the provided reference text. If any claim is not supported by the text, flag it explicitly as Not Grounded and explain briefly.' : '',
      'For EACH answer, provide:',
      '- correctedAnswer: corrected version with appropriate CEFR-level vocabulary/grammar',
      '- feedback.grammar: concise notes on grammar issues and fixes',
      '- feedback.vocabulary: word choice/register improvements per CEFR',
      '- feedback.spelling: any spelling/punctuation fixes',
      '- feedback.cohesion_coherence: structure/logic/flow suggestions',
      '- correct: one of "correct" | "partially_correct" | "incorrect" (based on the question and the reference text)',
      '- reason: one sentence explaining correctness judgment',
      '- grounded: one of "grounded" | "partially_grounded" | "not_grounded" (based on the reference text)',
      "- score: integer 0-100 (optional; holistic quality)",
      'Keep tone supportive and brief. Do not add extra keys.',
      'Return STRICT JSON ONLY with the exact shape: { "results": [ { "index": number, "correctedAnswer": string, "feedback": { "grammar": string, "vocabulary": string, "spelling": string, "cohesion_coherence": string }, "grounded": "grounded"|"partially_grounded"|"not_grounded", "correct": "correct"|"partially_correct"|"incorrect", "reason": string, "score": number } ] }',
      'Input data:',
      JSON.stringify({ level: lvl, referenceText, items: payload })
    ].join('\n');
  }

  async function handleSubmitReview() {
    setReviewError('');
    setIsReviewing(true);
    setShowReview(false);
    try {
      const prompt = buildEvaluationPrompt();
      const raw = await fetchOpenAIText({ prompt, temperature: 0.2, maxTokens: 1200 });
      let text = (raw || '').trim();
      // Strip fences if any
      if (text.startsWith('```')) {
        const m = text.match(/```[a-zA-Z]*\n([\s\S]*?)```/);
        if (m && m[1]) text = m[1].trim();
      }
      // Try direct parse
      let parsed = null;
      try {
        parsed = JSON.parse(text);
      } catch (_) {
        // Fallback: extract first {...} block
        const m2 = text.match(/\{[\s\S]*\}$/);
        if (m2) {
          try { parsed = JSON.parse(m2[0]); } catch (_) {}
        }
      }
      if (!parsed || !parsed.results || !Array.isArray(parsed.results)) {
        throw new Error('Invalid evaluation JSON');
      }
      // Normalize results by index
      const byIndex = new Map(parsed.results.map(r => [Number(r.index), r]));
      const ordered = editableQuestions.map((_, idx) => {
        const r = byIndex.get(idx) || {};
        const fb = r.feedback || {};
        return {
          index: idx,
          correctedAnswer: (r.correctedAnswer || '').toString(),
          score: typeof r.score === 'number' ? r.score : undefined,
          grounded: (r.grounded || '').toString(),
          correct: (r.correct || '').toString(),
          reason: (r.reason || '').toString(),
          feedback: {
            grammar: (fb.grammar || '').toString(),
            vocabulary: (fb.vocabulary || '').toString(),
            spelling: (fb.spelling || '').toString(),
            cohesion_coherence: (fb.cohesion_coherence || '').toString(),
          },
        };
      });
      setReviewResults(ordered);
      setShowReview(true);
      updateState({ answers: userAnswers, showReview: true, reviewResults: ordered });
    } catch (err) {
      setReviewError(err?.message || 'Failed to evaluate answers');
      setShowReview(false);
    } finally {
      setIsReviewing(false);
    }
  }

  const current = editableQuestions[currentQuestionIndex];
  const currentReview = showReview && Array.isArray(reviewResults)
    ? reviewResults.find(x => x.index === currentQuestionIndex)
    : null;

  return (
    <div className="oq-exercise-card">
      {/* Header */}
      <div className="oq-header">
        <div className="oq-header-left">
          <div className="oq-header-icon"><OQIcon /></div>
          {isEditing ? (
            <input
              className="oq-title-input"
              value={editableTitle}
              onChange={e => setEditableTitle(e.target.value)}
            />
          ) : (
            <span className="oq-title">{editableTitle}</span>
          )}
          <span className="oq-level-badge">{level}</span>
        </div>
        <div className="oq-header-actions">
          {!isEditing && !isStudent && (
            <button className="oq-icon-btn" aria-label="Edit" data-tip="Edit" onClick={() => setIsEditing(true)}>
              <EditIcon />
            </button>
          )}
          {isEditing && (
            <>
              <button className="oq-icon-btn is-save" aria-label="Save" data-tip="Save" onClick={handleSave}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9,12 11,14 15,10" />
                </svg>
              </button>
              <button className="oq-icon-btn is-cancel" aria-label="Cancel" data-tip="Cancel" onClick={handleCancel}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </>
          )}
          {onClose && (
            <button className="oq-icon-btn" onClick={onClose} aria-label="Close" data-tip="Close">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                <circle cx="12" cy="12" r="3" className="eye-pupil" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="oq-body">
        {/* Reference-text panel — shown when there IS one (or the teacher is
         * adding one in edit mode). This is what the AI review's "grounding"
         * check compares student answers against. */}
        {(isEditing || editableReferenceText) && (
          <div className={`oq-reference${referenceExpanded || isEditing ? ' is-open' : ''}`}>
            <button
              type="button"
              className="oq-reference-toggle"
              onClick={() => setReferenceExpanded(v => !v)}
              aria-expanded={referenceExpanded || isEditing}
            >
              <span className="oq-reference-toggle-icon" aria-hidden>📖</span>
              <span>{isEditing ? 'Source text' : (referenceExpanded ? 'Hide the text' : 'Read the text')}</span>
              {!isEditing && (
                <span className="oq-reference-toggle-caret" aria-hidden>{referenceExpanded ? '▲' : '▼'}</span>
              )}
            </button>
            {(referenceExpanded || isEditing) && (
              isEditing ? (
                <textarea
                  className="oq-reference-textarea"
                  value={editableReferenceText}
                  onChange={e => setEditableReferenceText(e.target.value)}
                  placeholder="Paste the source text the questions were generated from — the AI review checks student answers against this…"
                  rows={6}
                />
              ) : (
                <div className="oq-reference-body">
                  {editableReferenceText.split(/\n+/).map(p => p.trim()).filter(Boolean).map((para, i) => <p key={i}>{para}</p>)}
                </div>
              )
            )}
          </div>
        )}

        {current && (
          <div className="oq-question-card">
            <div className="oq-question-chip">
              Question {currentQuestionIndex + 1} of {editableQuestions.length}
            </div>
            <div className="oq-question-text">
              {isEditing ? (
                <input
                  type="text"
                  value={current.question || ''}
                  onChange={e => updateQuestionField(currentQuestionIndex, 'question', e.target.value)}
                  className="oq-question-input"
                  placeholder="Enter question..."
                />
              ) : (
                current.question || ''
              )}
            </div>

            {!isEditing && (
              <div className="oq-answer-section">
                <textarea
                  className="oq-answer-input"
                  placeholder="Write your answer here..."
                  value={userAnswer}
                  onChange={(e) => handleAnswerChange(currentQuestionIndex, e.target.value)}
                  rows={6}
                />
              </div>
            )}

            {isEditing ? (
              <div className="oq-edit-suggested">
                <label className="oq-edit-suggested-label">Suggested Answer:</label>
                <textarea
                  className="oq-answer-input"
                  value={current.suggestedAnswer || ''}
                  onChange={e => updateQuestionField(currentQuestionIndex, 'suggestedAnswer', e.target.value)}
                  rows={4}
                  placeholder="Enter suggested answer..."
                />
                <label className="oq-hide-suggested">
                  <input
                    type="checkbox"
                    checked={!!current.hideSuggestedAnswer}
                    onChange={e => updateHideSuggested(currentQuestionIndex, e.target.checked)}
                  />
                  Hide suggested answer
                </label>
              </div>
            ) : (
              !isStudent && !current.hideSuggestedAnswer && (
                <div className="oq-suggested-toggle-row">
                  <label className="oq-suggested-toggle">
                    <input
                      type="checkbox"
                      checked={showSuggested}
                      onChange={e => setShowSuggested(e.target.checked)}
                    />
                    Show suggested answer
                  </label>
                  {showSuggested && (
                    <div className="oq-suggested-answer">
                      <h4>Suggested Answer</h4>
                      <p>{current.suggestedAnswer || ''}</p>
                    </div>
                  )}
                </div>
              )
            )}

            {/* Per-question AI feedback under the current question */}
            {currentReview && !isEditing && (
              <div className="oq-review">
                <h3 className="oq-review-title">Feedback for question {currentReview.index + 1}</h3>
                <div className="oq-review-chips">
                  {currentReview.correct && (
                    <span className={`oq-chip ${currentReview.correct === 'correct' ? 'ok' : currentReview.correct === 'partially_correct' ? 'warn' : 'err'}`}>
                      {currentReview.correct === 'correct' ? 'Correct' : currentReview.correct === 'partially_correct' ? 'Partially correct' : 'Incorrect'}
                    </span>
                  )}
                  {currentReview.grounded && (
                    <span className={`oq-chip ${currentReview.grounded === 'grounded' ? 'ok' : currentReview.grounded === 'partially_grounded' ? 'warn' : 'err'}`}>
                      {currentReview.grounded === 'grounded' ? 'Grounded' : currentReview.grounded === 'partially_grounded' ? 'Partially grounded' : 'Not grounded'}
                    </span>
                  )}
                  {typeof currentReview.score === 'number' && (
                    <span className="oq-chip score-chip">Score: {currentReview.score}/100</span>
                  )}
                </div>
                {currentReview.reason && (
                  <div className="oq-review-reason">{currentReview.reason}</div>
                )}
                {currentReview.correctedAnswer && (
                  <div className="oq-review-corrected">
                    <strong>Corrected answer:</strong>
                    <div className="oq-review-corrected-text">{currentReview.correctedAnswer}</div>
                  </div>
                )}
                <ul className="oq-review-feedback">
                  {currentReview.feedback?.grammar && <li><strong>Grammar:</strong> {currentReview.feedback.grammar}</li>}
                  {currentReview.feedback?.vocabulary && <li><strong>Vocabulary:</strong> {currentReview.feedback.vocabulary}</li>}
                  {currentReview.feedback?.spelling && <li><strong>Spelling:</strong> {currentReview.feedback.spelling}</li>}
                  {currentReview.feedback?.cohesion_coherence && <li><strong>Cohesion &amp; Coherence:</strong> {currentReview.feedback.cohesion_coherence}</li>}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="navigation-buttons">
          {currentQuestionIndex > 0 ? (
            <button className="nav-btn prev-btn" onClick={handlePrevious}>Previous</button>
          ) : <div className="oq-nav-placeholder" />}

          <div className="question-dots">
            <div
              ref={dotsScrollRef}
              className="question-dots-scroll"
              style={{
                maxWidth: Math.round((Math.min(editableQuestions.length, 5) * 40) + ((Math.min(editableQuestions.length, 5) - 1) * 8)),
                WebkitMaskImage: (() => {
                  const pad = 28;
                  if (showLeftFade && showRightFade) {
                    return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  }
                  if (showLeftFade && !showRightFade) {
                    return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 100%)`;
                  }
                  if (!showLeftFade && showRightFade) {
                    return `linear-gradient(to right, #000 0%, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  }
                  return 'none';
                })(),
                maskImage: (() => {
                  const pad = 28;
                  if (showLeftFade && showRightFade) {
                    return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  }
                  if (showLeftFade && !showRightFade) {
                    return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 100%)`;
                  }
                  if (!showLeftFade && showRightFade) {
                    return `linear-gradient(to right, #000 0%, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  }
                  return 'none';
                })()
              }}
            >
              {editableQuestions.map((_, index) => (
                <div key={index} className="oq-dot-wrap">
                  <button
                    className={`dot ${index === currentQuestionIndex ? 'active' : ''} ${userAnswers[index] ? 'answered' : ''}`}
                    onClick={() => { setCurrentQuestionIndex(index); updateState({ currentQuestion: index }); }}
                  >
                    {index + 1}
                  </button>
                  {isEditing && editableQuestions.length > 1 && (
                    <button
                      className="oq-dot-delete"
                      onClick={(e) => { e.stopPropagation(); handleDeleteQuestion(index); }}
                      title="Delete question"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            {isEditing && editableQuestions.length < MAX_QUESTIONS && (
              <button ref={addBtnRef} className="dot add-dot" onClick={handleAddQuestion} title="Add question" type="button">
                +
              </button>
            )}
          </div>

          {currentQuestionIndex < editableQuestions.length - 1 ? (
            <button className="nav-btn next-btn" onClick={handleNext}>Next</button>
          ) : <div className="oq-nav-placeholder" />}
        </div>

        {isEditing && editableQuestions.length >= MAX_QUESTIONS && (
          <div className="oq-max-hint">Maximum of {MAX_QUESTIONS} questions per exercise</div>
        )}

        {/* Submit & Review section (button + global error) */}
        {!isEditing && !showReview && (
          <div className="oq-submit-wrapper">
            <button
              className="oq-submit-btn"
              onClick={handleSubmitReview}
              disabled={!allAnswered || isReviewing}
            >
              {isReviewing ? 'Reviewing…' : 'Submit Answers'}
            </button>
            <div className="oq-submit-tooltip">
              {!allAnswered
                ? 'Please answer all questions'
                : 'AI will check grounding, correctness, and give feedback'}
            </div>
          </div>
        )}
        {!isEditing && showReview && (
          <button className="oq-try-again-btn" onClick={handleTryAgain}>Try Again</button>
        )}
        {reviewError && !isEditing && (
          <div className="oq-review-error">{reviewError}</div>
        )}
      </div>
    </div>
  );
};

export default OpenQuestionsExercise;
